import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, AlertCircle, Shuffle, CheckCircle2, X, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MEALS = ["breakfast", "lunch", "dinner", "snack"];

const SwapSheet = ({ token, slot, reasons, onClose, onDone }) => {
  const [opts, setOpts] = useState(null);
  const [reason, setReason] = useState("");
  const [choice, setChoice] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/my-plan/${token}/swap-options?day=${slot.day}&meal_type=${slot.mealType}`).then(r => r.json()).then(d => setOpts(d.options || [])).catch(() => setOpts([]));
  }, [token, slot]);

  const submit = async () => {
    if (!reason) { toast.error("Please tell us why you'd like to swap."); return; }
    if (!choice) { toast.error("Please choose a meal, or let your dietitian decide."); return; }
    setSaving(true);
    try {
      const option = choice === "dietitian" ? null : { name: choice.name, items: choice.items.map(({ label, ...i }) => i) };
      const r = await fetch(`${API}/my-plan/${token}/swap-requests`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ day: slot.day, meal_type: slot.mealType, reason, note, option }) });
      const d = await r.json();
      if (r.ok) { toast.success(d.message); onDone(); } else toast.error(d.detail || "Could not send your request.");
    } catch { toast.error("Connection problem."); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center" data-testid="swap-sheet">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-5 max-h-[90vh] overflow-y-auto space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-bold text-[#FF7A00] uppercase tracking-widest">{slot.dayName} {slot.mealType}</p>
            <h2 className="text-lg font-bold text-stone-900">Swap "{slot.current}"</h2>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700" data-testid="swap-sheet-close"><X className="w-5 h-5" /></button>
        </div>
        <div>
          <p className="text-sm font-semibold text-stone-800 mb-2">Why would you like to swap it?</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(reasons).map(([k, v]) => (
              <button key={k} onClick={() => setReason(k)} data-testid={`swap-reason-${k}`} className={`text-xs px-3 py-2 rounded-full border transition-colors ${reason === k ? "bg-[#FF7A00] text-white border-[#FF7A00]" : "bg-white border-stone-200 text-stone-700 hover:border-[#FF7A00]/50"}`}>{v}</button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-sm font-semibold text-stone-800 mb-2">Choose a meal you'd prefer</p>
          {!opts ? <Loader2 className="w-5 h-5 animate-spin text-[#FF7A00]" /> : (
            <div className="space-y-2">
              {opts.map((o, i) => (
                <button key={o.name} onClick={() => setChoice(o)} data-testid={`patient-swap-option-${i}`} className={`w-full text-left rounded-xl border p-3 transition-colors ${choice?.name === o.name ? "border-[#FF7A00] bg-[#FFF7F0]" : "border-stone-200 hover:border-[#FF7A00]/50"}`}>
                  <p className="text-sm font-semibold text-stone-900">{o.name}</p>
                  <p className="text-xs text-stone-500">{o.items.map(it => `${it.qty} ${it.label}`).join(", ")}</p>
                </button>
              ))}
              <button onClick={() => setChoice("dietitian")} data-testid="patient-swap-dietitian" className={`w-full text-left rounded-xl border p-3 transition-colors ${choice === "dietitian" ? "border-[#FF7A00] bg-[#FFF7F0]" : "border-dashed border-stone-300 hover:border-[#FF7A00]/50"}`}>
                <p className="text-sm font-semibold text-stone-900">Let my dietitian decide</p>
                <p className="text-xs text-stone-500">Your dietitian will choose a suitable meal for you.</p>
              </button>
            </div>
          )}
        </div>
        <Textarea placeholder="Anything else your dietitian should know? (optional)" value={note} onChange={e => setNote(e.target.value)} className="rounded-xl" data-testid="swap-note" />
        <Button onClick={submit} disabled={saving} className="w-full bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-xl h-12 font-bold" data-testid="swap-submit">
          {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Shuffle className="w-5 h-5 mr-2" />}Send request to my dietitian
        </Button>
      </div>
    </div>
  );
};

const MealCard = ({ mealType, meal, pending, onSwap, testid }) => (
  <div className="py-3 border-b border-stone-100 last:border-0" data-testid={testid}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[11px] font-bold text-[#FF7A00] uppercase tracking-wider">{mealType}{meal.time ? ` · ${meal.time}` : ""}</p>
        <p className="text-sm font-semibold text-stone-900">{meal.name}</p>
        {meal.items.length > 0 && <p className="text-xs text-stone-500 mt-0.5">{meal.items.map(i => `${i.qty} ${i.label}`).join(", ")}</p>}
      </div>
      {pending ? (
        <span className="shrink-0 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 flex items-center gap-1" data-testid={`${testid}-pending`}><Clock className="w-3 h-3" />Swap requested</span>
      ) : meal.items.length > 0 && (
        <button onClick={onSwap} className="shrink-0 text-xs font-semibold text-[#FF7A00] border border-[#FF7A00]/40 rounded-full px-3 py-1.5 hover:bg-[#FF7A00]/5 flex items-center gap-1" data-testid={`${testid}-swap`}><Shuffle className="w-3 h-3" />Swap</button>
      )}
    </div>
  </div>
);

export default function MyPlanSwap() {
  const { token } = useParams();
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);
  const [slot, setSlot] = useState(null);

  const load = () => fetch(`${API}/my-plan/${token}`).then(async r => { const d = await r.json(); if (r.ok) setPlan(d); else setError(d.detail); }).catch(() => setError("Connection problem."));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <div className="min-h-screen bg-[#FAFAF9] py-10 px-4"><Card className="max-w-md mx-auto rounded-2xl p-8 text-center" data-testid="my-plan-error"><AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" /><p className="text-sm text-stone-600">{error}</p></Card></div>;
  if (!plan) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#FF7A00]" /></div>;

  return (
    <div className="min-h-screen bg-[#FAFAF9] py-8 px-4" data-testid="my-plan-page">
      <div className="max-w-2xl mx-auto space-y-5">
        <img src="/platemind-logo-new.png" alt="PlateMind" className="h-9 w-auto" />
        <div>
          <p className="text-xs font-bold text-[#FF7A00] uppercase tracking-widest">Your meal plan</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-stone-900 mt-1">Hello {plan.first_name}, here's your plan</h1>
          <p className="text-sm text-stone-500 mt-2">Tap <strong>Swap</strong> on any meal you don't like or can't easily get. Your dietitian will review your request and email you when it's done.</p>
        </div>
        {plan.pending.length > 0 && (
          <Card className="rounded-xl p-3 bg-amber-50 border-amber-200 text-sm text-amber-900 flex items-center gap-2" data-testid="pending-requests-note"><CheckCircle2 className="w-4 h-4" />{plan.pending.length} swap request{plan.pending.length > 1 ? "s" : ""} waiting for your dietitian.</Card>
        )}
        {plan.days.map(d => (
          <Card key={d.day} className="rounded-2xl p-4 border-stone-200" data-testid={`my-plan-day-${d.day}`}>
            <p className="text-sm font-bold text-stone-900">Day {d.day} · {d.day_name}{d.week ? <span className="text-stone-400 font-normal"> (week {d.week})</span> : null}</p>
            {MEALS.filter(mt => d.meals[mt]).map(mt => (
              <MealCard key={mt} mealType={mt} meal={d.meals[mt]} pending={plan.pending.includes(`${d.day}-${mt}`)} testid={`meal-${d.day}-${mt}`}
                onSwap={() => setSlot({ day: d.day, dayName: d.day_name, mealType: mt, current: d.meals[mt].name })} />
            ))}
          </Card>
        ))}
        <p className="text-[11px] text-stone-400 text-center">Questions about your plan? Message PlateMind on WhatsApp: +234 706 203 9281</p>
      </div>
      {slot && <SwapSheet token={token} slot={slot} reasons={plan.reasons} onClose={() => setSlot(null)} onDone={() => { setSlot(null); load(); }} />}
    </div>
  );
}
